/* Cart drawer behaviour + the add-to-cart state machine.

   Every mutation goes through the Cart AJAX API with `sections: 'opt-cart-drawer'`
   so Shopify returns freshly rendered Liquid for the drawer in the same round trip.
   That keeps money formatting, the package grouping, the per-day maths and the
   free-shipping logic in one place (snippets/opt-cart-body.liquid) instead of
   duplicated here.

   The package block has no per-jar stepper by design: a PERUSTA subscription is ten
   cart lines and letting someone decrement a single jar silently breaks the pack.
   Removing the package removes every one of its keys in a single /cart/update.js.

   OptCart.submit() owns the add-to-cart feel: pending -> success -> drawer. The PDP
   keeps ownership of the PAYLOAD (it builds the ten-line subscription body) and
   hands it here, so button states, error copy and drawer opening live in one file
   rather than being reimplemented by every buy button on the page.

   ============ 7.9.2026: ONE Meta AddToCart PER CLICK ============
   Ads Manager showed AddToCart counts several times higher than page views. Cause:
   Shopify's web-pixels manager wraps window.fetch, watches /cart/add.js and emits
   one product_added_to_cart per cart LINE. A PERUSTA subscription is five lines
   (4 jars + gift), so the Facebook & Instagram channel pixel logged FIVE AddToCart
   events for one click (the "175 add-to-carts" were roughly 35 shoppers).
   Fix, in two parts:
     1. submit() sends the add through cleanFetch(): the fetch of a same-origin
        about:blank iframe that no third party has patched. Cookies travel, the
        cart updates, the pixel manager sees nothing.
     2. metaAddToCart() emits exactly one AddToCart with the real basket value
        (144,00 tilaus / 160,00 kertaosto), same pixel id as the channel, via
        trackSingle with autoConfig off, only with marketing consent.
   The channel pixel keeps PageView, ViewContent, InitiateCheckout and Purchase
   (plus Conversions API) untouched. In-cart upsells (post()) deliberately keep the
   normal fetch: one item, one event, nothing to dedupe.
   =================================================================== */
(function () {
  var SECTION = 'opt-cart-drawer';
  var MIN_BUSY = 260;   /* below this a spinner reads as a flicker, not feedback */
  var OK_HOLD = 620;    /* long enough to register the tick, short enough not to nag */
  var META_PIXEL = '868528916192336'; /* same id the Facebook & Instagram channel uses */
  var drawer, scrim, scroll, lastFocus = null;

  function el(id) { return document.getElementById(id); }

  /* The open transition needs one paint between `hidden = false` and adding .on,
     or the drawer jumps instead of sliding. requestAnimationFrame gives that, but
     rAF is FULLY PAUSED in a backgrounded tab, which left the drawer stranded:
     scrim unhidden, aria-hidden already false, but .on never applied and no way to
     recover on return. The timeout is the floor, so the drawer always opens. */
  function nextFrame(fn) {
    var done = false;
    var run = function () { if (done) return; done = true; fn(); };
    requestAnimationFrame(run);
    setTimeout(run, 40);
  }

  function refs() {
    drawer = el('OptCartDrawer');
    scrim = el('OptCartScrim');
    scroll = el('OptCartScroll');
    return !!drawer;
  }

  function focusables() {
    return drawer.querySelectorAll('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex=\"-1\"])');
  }

  function open() {
    if (!refs()) return;
    lastFocus = document.activeElement;
    scrim.hidden = false;
    nextFrame(function () {
      scrim.classList.add('on');
      drawer.classList.add('on');
    });
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var f = focusables();
    (f.length ? f[0] : drawer).focus();
  }

  function close() {
    if (!refs()) return;
    drawer.classList.remove('on');
    scrim.classList.remove('on');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTimeout(function () { if (scrim) scrim.hidden = true; }, 280);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function busy(on) { if (drawer) drawer.classList.toggle('is-busy', !!on); }

  /* ---------- clean fetch (see header) ----------
     Falls back to the ordinary fetch if the iframe cannot be created, so an add
     never fails because of tracking plumbing. The iframe is removed only after the
     body has been read: a Response from a detached realm cannot be consumed. */
  function readJson(r) {
    return r.text().then(function (t) {
      var d = null;
      try { d = JSON.parse(t); } catch (e) {}
      return { status: r.status, data: d };
    });
  }
  function dropFrame(f) {
    try { if (f && f.parentNode) f.parentNode.removeChild(f); } catch (e) {}
  }
  function cleanFetch(url, opts) {
    var f = null;
    try {
      f = document.createElement('iframe');
      f.setAttribute('aria-hidden', 'true');
      f.tabIndex = -1;
      f.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
      document.body.appendChild(f);
      var w = f.contentWindow;
      if (!w || typeof w.fetch !== 'function') throw new Error('no iframe fetch');
      return w.fetch(url, opts)
        .then(readJson)
        .then(function (out) { dropFrame(f); return out; }, function (e) { dropFrame(f); throw e; });
    } catch (e) {
      dropFrame(f);
      return fetch(url, opts).then(readJson);
    }
  }

  /* ---------- Meta AddToCart, exactly once per click (see header) ----------
     Consent rule = the channel pixel's rule in the EU: marketing consent or no
     event. Not a fallback, not a "necessary" cookie. */
  function marketingAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return false;
      if (typeof cp.marketingAllowed === 'function') return !!cp.marketingAllowed();
      var c = typeof cp.currentVisitorConsent === 'function' ? cp.currentVisitorConsent() : null;
      return !!(c && c.marketing === 'yes');
    } catch (e) { return false; }
  }
  function loadFbq() {
    if (window.fbq) return window.fbq;
    /* standard Meta base code, minus the automatic PageView: the channel pixel
       already sends it and a second one would double every page view */
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n;
      n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = true; t.src = v;
      s = b.getElementsByTagName(e)[0];
      if (s && s.parentNode) s.parentNode.insertBefore(t, s); else b.head.appendChild(t);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('set', 'autoConfig', false, META_PIXEL);
    window.fbq('init', META_PIXEL);
    return window.fbq;
  }
  function metaAddToCart(payload, items) {
    try {
      if (!items || !items.length || !marketingAllowed()) return;
      var fbq = loadFbq();
      /* /cart/add.js echoes the cart LINES after the add, so quantity and
         final_line_price are cumulative when the same variant was already in the
         cart (a second click would report 2x). Quantities therefore come from the
         payload we sent; the response only supplies unit prices (selling plan
         applied: 36,00 per jar on tilaus) and product ids. */
      var unit = {}, pidOf = {};
      items.forEach(function (i) {
        var k = String(i.variant_id || i.id);
        unit[k] = (i.final_price != null ? i.final_price : (i.price || 0)) / 100;
        pidOf[k] = String(i.product_id || k);
      });
      var wanted = (payload && payload.items && payload.items.length) ? payload.items
        : items.map(function (i) { return { id: i.variant_id || i.id, quantity: 1 }; });
      var value = 0, count = 0, ids = [], contents = [];
      wanted.forEach(function (w) {
        var k = String(w.id), qty = w.quantity || 1;
        var u = unit[k] != null ? unit[k] : 0, pid = pidOf[k] || k;
        value += u * qty; count += qty;
        if (ids.indexOf(pid) < 0) ids.push(pid);
        contents.push({ id: pid, quantity: qty, item_price: u });
      });
      fbq('trackSingle', META_PIXEL, 'AddToCart', {
        content_type: 'product',
        content_ids: ids,
        contents: contents,
        num_items: count,
        value: Math.round(value * 100) / 100,
        currency: 'EUR'
      }, { eventID: 'atc-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8) });
    } catch (e) {}
  }

  /* ---------- Klaviyo Added to Cart ----------
     Fired HERE, not by patching window.fetch. snippets/opt-atc-track.liquid used to
     wrap fetch at page load; Klaviyo's own onsite bundle and Shopify's web-pixels
     manager wrap fetch too, they load later, and the last wrapper wins. Verified
     live 2.9.2026: window.fetch was a minified third-party wrapper with no mention
     of cart/add, and a real add produced zero klaviyo pushes - which is why metric
     XpNmvz had 2 events ever and flow WWD5C6 never sent a single email while the
     Meta pixel logged 175 add-to-carts.

     Every add path in this theme funnels through this file, so calling from here
     cannot lose a load-order race. Payload shape matches what Klaviyo's cart
     templates expect (Items[], CheckoutURL, $value).

     Klaviyo only accepts events for browsers it has identified - popup signup,
     email-link click, or purchase history. Anonymous adders cannot be emailed by
     anyone, so this will always read lower than the pixel. */
  function trackAdd() {
    try {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart || !cart.items || !cart.items.length) return;
          window.klaviyo = window.klaviyo || [];
          if (typeof window.klaviyo.push !== 'function') return;
          window.klaviyo.push(['track', 'Added to Cart', {
            $value: (cart.total_price || 0) / 100,
            ItemNames: cart.items.map(function (i) { return i.product_title; }),
            CheckoutURL: window.location.origin + '/cart',
            Items: cart.items.map(function (i) {
              return {
                ProductID: i.product_id,
                VariantID: i.variant_id,
                Name: i.product_title,
                Quantity: i.quantity,
                ItemPrice: (i.price || 0) / 100,
                RowTotal: (i.final_line_price || 0) / 100,
                ImageURL: i.image,
                URL: window.location.origin + i.url
              };
            })
          }]);
        })
        .catch(function () {});
    } catch (e) {}
  }

  /* Swap in the re-rendered section. The API returns the whole section wrapper,
     so pull out our scroll container and the header count from it. */
  function apply(html) {
    if (!html || !refs()) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var freshScroll = doc.getElementById('OptCartScroll');
    var freshTop = doc.querySelector('.opt-cd__top h2');
    if (freshScroll && scroll) scroll.innerHTML = freshScroll.innerHTML;
    var topH2 = drawer.querySelector('.opt-cd__top h2');
    if (freshTop && topH2) topH2.textContent = freshTop.textContent;
    syncCount();
  }

  function syncCount() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        document.querySelectorAll('[data-cart-count]').forEach(function (n) {
          n.textContent = c.item_count;
          n.style.display = c.item_count > 0 ? '' : 'none';
        });
      })
      .catch(function () {});
  }

  function post(url, body) {
    body.sections = SECTION;
    busy(true);
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (d && d.sections && d.sections[SECTION]) apply(d.sections[SECTION]);
        busy(false);
        return d;
      })
      .catch(function () { busy(false); flash('Päivitys ei onnistunut. Yritä uudelleen.'); });
  }

  /* ---------- add-to-cart state machine ---------- */

  function btnList(btns) {
    if (!btns) return [];
    if (btns.nodeType) return [btns];
    return Array.prototype.slice.call(btns);
  }

  function setState(btns, state, label) {
    btnList(btns).forEach(function (b) {
      if (!b.dataset.atcLabel) b.dataset.atcLabel = b.textContent.trim();
      b.classList.remove('is-atc-busy', 'is-atc-ok');
      if (state === 'busy') {
        b.classList.add('is-atc-busy');
        b.disabled = true;
        b.setAttribute('aria-busy', 'true');
      } else if (state === 'ok') {
        b.classList.add('is-atc-ok');
        b.disabled = true;
        b.removeAttribute('aria-busy');
        b.textContent = label || 'Lisätty ostoskoriin';
      } else {
        b.disabled = false;
        b.removeAttribute('aria-busy');
        b.textContent = b.dataset.atcLabel;
      }
    });
  }

  /* Errors are never an alert(): a modal dialog on top of a checkout flow is the
     fastest way to lose the sale. Short, human, dismissible, and the button always
     returns to a usable state. */
  function flash(msg) {
    var t = el('OptCartToast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'OptCartToast';
      t.className = 'opt-cd__toast';
      t.setAttribute('role', 'alert');
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('on');
    clearTimeout(t._timer);
    t._timer = setTimeout(function () { t.classList.remove('on'); }, 4200);
  }

  function reason(d, status) {
    if (status === 422 || (d && d.status === 422)) {
      return (d && d.description) || 'Tuotetta ei voi juuri nyt lisätä. Tarkista saatavuus.';
    }
    if (d && d.description) return d.description;
    return 'Lisääminen ei onnistunut. Yritä uudelleen.';
  }

  function submit(payload, btns) {
    var started = Date.now();
    setState(btns, 'busy');
    return cleanFetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (out) {
        var d = out.data, code = out.status;
        if (!d || !d.items || code >= 400) throw new Error(reason(d, code));
        trackAdd();
        metaAddToCart(payload, d.items);
        /* hold the spinner to a minimum so it reads as deliberate, not janky */
        var wait = Math.max(0, MIN_BUSY - (Date.now() - started));
        return new Promise(function (res) { setTimeout(res, wait); }).then(function () {
          setState(btns, 'ok');
          return window.OptCart.refresh();
        }).then(function () {
          return new Promise(function (res) { setTimeout(res, OK_HOLD); });
        }).then(function () {
          if (refs()) { open(); setState(btns, 'idle'); }
          else { window.location.href = '/cart'; }   /* never a dead end */
        });
      })
      .catch(function (e) {
        setState(btns, 'idle');
        flash(e && e.message ? e.message : 'Verkkovirhe. Yritä uudelleen.');
      });
  }

  /* Public: the PDP builds its own subscription payload and hands it to submit(). */
  window.OptCart = {
    open: open,
    close: close,
    submit: submit,
    flash: flash,
    track: trackAdd,
    refresh: function () {
      return fetch('/?sections=' + SECTION, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (d) { apply(d[SECTION]); })
        .catch(function () {});
    },
    openWithRefresh: function () {
      return window.OptCart.refresh().then(open);
    }
  };

  document.addEventListener('click', function (e) {
    var t = e.target;

    if (t.closest('[data-cart-open]')) { e.preventDefault(); window.OptCart.openWithRefresh(); return; }
    if (t.closest('[data-cart-close]') || t.id === 'OptCartScrim') { e.preventDefault(); close(); return; }

    /* In-cart upsell. Adding re-renders the section, which replaces this whole row
       with an ordinary line + stepper, so there is no "added" state to design and
       no local state to keep. */
    var add = t.closest('[data-cart-add]');
    if (add) {
      e.preventDefault();
      var vid = parseInt(add.dataset.variant, 10);
      if (!vid || add.disabled) return;
      add.disabled = true;
      add.classList.add('is-adding');
      post('/cart/add.js', { items: [{ id: vid, quantity: 1 }] }).then(function () { trackAdd(); });
      return;
    }

    var qty = t.closest('[data-cart-qty]');
    if (qty) {
      e.preventDefault();
      post('/cart/change.js', { id: qty.dataset.key, quantity: parseInt(qty.dataset.cartQty, 10) });
      return;
    }

    var rmPack = t.closest('[data-cart-remove-pack]');
    if (rmPack) {
      e.preventDefault();
      var holder = rmPack.closest('[data-pack-keys]');
      if (!holder) return;
      var updates = {};
      holder.dataset.packKeys.split(',').forEach(function (k) { if (k) updates[k] = 0; });
      post('/cart/update.js', { updates: updates });
    }
  });

  document.addEventListener('keydown', function (e) {
    if (!drawer || !drawer.classList.contains('on')) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refs);
  else refs();
})();